import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Info, ListPlus, Minus, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { TaskCreateDialog } from '@/features/patient-record/components/panels/TasksPanel/TaskCreateDialog';
import { useResolveUserDisplayNames } from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import type { CarePathwayStepViewModel } from '@/features/patient-record/types';

export interface StepCompletionPayload {
  readonly stepId: string;
  readonly completionNote?: string;
}

export interface StepSkipPayload {
  readonly stepId: string;
  readonly skipRationale: string;
}

interface PathwayStepListProps {
  readonly guid: string;
  readonly steps: readonly CarePathwayStepViewModel[];
  readonly updatingStepId: string | null;
  readonly pathwayClosed: boolean;
  readonly onRequestComplete: (payload: StepCompletionPayload) => void;
  readonly onRequestSkip: (payload: StepSkipPayload) => void;
  readonly onMarkIncomplete: (stepId: string) => void;
}

export function PathwayStepList({
  guid,
  steps,
  updatingStepId,
  pathwayClosed,
  onRequestComplete,
  onRequestSkip,
  onMarkIncomplete,
}: PathwayStepListProps): ReactElement | null {
  const inProgressId = useMemo(
    () => steps.find((s) => s.status === 'IN_PROGRESS')?.id ?? null,
    [steps],
  );
  const [expandedId, setExpandedId] = useState<string | null>(inProgressId);
  const [taskForStep, setTaskForStep] = useState<CarePathwayStepViewModel | null>(null);

  if (steps.length === 0) return null;

  return (
    <>
      <ol className="flex flex-col divide-y divide-border/60 rounded-md border border-border">
        {steps.map((step, idx) => (
          <StepRow
            key={step.id}
            index={idx + 1}
            step={step}
            isExpanded={expandedId === step.id}
            busy={updatingStepId === step.id}
            pathwayClosed={pathwayClosed}
            onToggle={() => {
              setExpandedId((id) => (id === step.id ? null : step.id));
            }}
            onRequestComplete={onRequestComplete}
            onRequestSkip={onRequestSkip}
            onMarkIncomplete={onMarkIncomplete}
            onCreateTask={() => {
              setTaskForStep(step);
            }}
          />
        ))}
      </ol>
      {taskForStep !== null && (
        <TaskCreateDialog
          guid={guid}
          defaults={
            taskForStep.description.length > 0
              ? { title: taskForStep.name, description: taskForStep.description }
              : { title: taskForStep.name }
          }
          onClose={() => {
            setTaskForStep(null);
          }}
        />
      )}
    </>
  );
}

interface StepRowProps {
  readonly index: number;
  readonly step: CarePathwayStepViewModel;
  readonly isExpanded: boolean;
  readonly busy: boolean;
  readonly pathwayClosed: boolean;
  readonly onToggle: () => void;
  readonly onRequestComplete: (payload: StepCompletionPayload) => void;
  readonly onRequestSkip: (payload: StepSkipPayload) => void;
  readonly onMarkIncomplete: (stepId: string) => void;
  readonly onCreateTask: () => void;
}

function StepRow({
  index,
  step,
  isExpanded,
  busy,
  pathwayClosed,
  onToggle,
  onRequestComplete,
  onRequestSkip,
  onMarkIncomplete,
  onCreateTask,
}: StepRowProps): ReactElement {
  const { t } = useTranslation();
  const isOpen = step.status === 'NOT_STARTED' || step.status === 'IN_PROGRESS';
  const isCurrent = step.status === 'IN_PROGRESS';
  const isSkipped = step.status === 'SKIPPED';

  return (
    <li className={cn('px-3 py-2.5', isCurrent && 'bg-primary/5')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-start gap-3 text-left"
      >
        <StepIndicator status={step.status} />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm font-medium',
              isSkipped ? 'text-muted-foreground line-through' : 'text-card-foreground',
            )}
          >
            <span className="text-muted-foreground tabular-nums mr-1.5">{index}.</span>
            {step.name}
          </p>
          <StepSubLine step={step} />
        </div>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-4 w-4 text-muted-foreground mt-1 shrink-0 transition-transform',
            isExpanded && 'rotate-180',
          )}
        />
      </button>
      {isExpanded && (
        <div className="mt-2 ml-11 flex flex-col gap-3">
          {step.description.length > 0 && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          )}
          {step.status === 'COMPLETED' && step.completionNote !== null && (
            <NoteCallout
              label={t('patient.carePathways.completionNoteLabel')}
              text={step.completionNote}
            />
          )}
          {step.status === 'SKIPPED' && step.skipRationale !== null && (
            <NoteCallout
              label={t('patient.carePathways.skipRationaleLabel')}
              text={step.skipRationale}
            />
          )}
          {!pathwayClosed && (
            <div className="flex flex-wrap items-center gap-2">
              {isOpen ? (
                <>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      onRequestComplete({ stepId: step.id });
                    }}
                  >
                    <Check className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                    {t('patient.carePathways.markComplete')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={onCreateTask}>
                    <ListPlus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                    {t('patient.carePathways.createTask')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      onRequestSkip({ stepId: step.id, skipRationale: '' });
                    }}
                    className="text-muted-foreground"
                  >
                    {t('patient.carePathways.skipStep')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      onMarkIncomplete(step.id);
                    }}
                  >
                    {t('patient.carePathways.markIncomplete')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onCreateTask}>
                    <ListPlus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                    {t('patient.carePathways.createTask')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function StepIndicator({
  status,
}: {
  readonly status: CarePathwayStepViewModel['status'];
}): ReactElement {
  if (status === 'COMPLETED') {
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0"
      >
        <Check className="h-4 w-4" />
      </span>
    );
  }
  if (status === 'SKIPPED') {
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground shrink-0"
      >
        <Minus className="h-4 w-4" />
      </span>
    );
  }
  if (status === 'IN_PROGRESS') {
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary text-primary shrink-0"
      >
        <Play className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shrink-0"
    >
      <Info className="h-3.5 w-3.5" />
    </span>
  );
}

function StepSubLine({
  step,
}: {
  readonly step: CarePathwayStepViewModel;
}): ReactElement | null {
  const { t } = useTranslation();
  // PTV2-1509: resolve the completer guid to a display name (same join + fallback as the
  // Care Team panel) so the raw guid is never rendered. Called before the status branches
  // to keep hook order stable.
  const completedByGuid = step.status === 'COMPLETED' ? step.completedBy : null;
  const completerNames = useResolveUserDisplayNames(
    completedByGuid !== null ? [completedByGuid] : [],
  );
  if (step.status === 'COMPLETED' && step.completedAt !== null) {
    const date = formatDate(step.completedAt);
    const completer = completedByGuid !== null ? completerNames.get(completedByGuid) : undefined;
    // No completer → "—"; lookup in flight → date only; lookup failed → "Unknown user".
    let line: string;
    if (completedByGuid === null) {
      line = t('patient.carePathways.stepCompletedMeta', { date, author: '—' });
    } else if (completer === undefined || completer.status === 'pending') {
      line = t('patient.carePathways.stepCompletedMetaDateOnly', { date });
    } else {
      line = t('patient.carePathways.stepCompletedMeta', {
        date,
        author: completer.name ?? t('patient.careTeam.unknownUser'),
      });
    }
    return <p className="text-xs text-muted-foreground mt-0.5">{line}</p>;
  }
  if (step.status === 'SKIPPED') {
    return (
      <p className="text-xs text-muted-foreground mt-0.5">
        {t('patient.carePathways.stepStatus.SKIPPED')}
      </p>
    );
  }
  if (step.status === 'IN_PROGRESS') {
    return (
      <p className="text-[11px] font-semibold uppercase tracking-wider text-primary mt-0.5">
        {t('patient.carePathways.inProgress')}
      </p>
    );
  }
  return (
    <p className="text-xs text-muted-foreground mt-0.5">
      {t('patient.carePathways.notStarted')}
    </p>
  );
}

function NoteCallout({
  label,
  text,
}: {
  readonly label: string;
  readonly text: string;
}): ReactElement {
  return (
    <div className="rounded-md border border-border bg-muted/40 px-2.5 py-1.5">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </p>
      <p className="text-xs text-foreground mt-0.5 whitespace-pre-wrap">{text}</p>
    </div>
  );
}
